import { computed, inject, Injector, Service } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import type {
  OnyxMaybeArray,
  OnyxOptional,
  OnyxOptionsGroup,
} from '@onyx/angular';
import {
  ActionHelper,
  DropdownHelper,
  OnyxModalService,
  OnyxToastService,
} from '@onyx/angular';
import { castArray, chain, isString } from 'lodash';
import { DateTime } from 'luxon';
import { AuthStore } from '../../../../auth/common/stores/auth.store';
import { DAY_TIME_RANGE } from '../../../../common/constants/common/day-time-range';
import { CommonHelper } from '../../../../common/helpers/common.helper';
import { NavigationHelper } from '../../../../common/helpers/navigation.helper';
import type { TelematicsDelegation } from '../../../../common/interfaces/telematics/telematics-delegation';
import type { TelematicsDriver } from '../../../../common/interfaces/telematics/telematics-driver';
import type { TelematicsItem } from '../../../../common/interfaces/telematics/telematics-item';
import type { HelperOptions } from '../../../../common/interfaces/utilities/helper-options';
import { FleetTypeHelper } from '../../../fleet/common/helpers/fleet-type.helper';
import type { DelegationActionModalData } from '../../delegation-action-modal/delegation-action-modal.component';
import {
  DelegationActionModalComponent,
  DelegationActionModalMode,
} from '../../delegation-action-modal/delegation-action-modal.component';
import type { DelegationModalData } from '../../delegation-modal/delegation-modal.component';
import { DelegationModalComponent } from '../../delegation-modal/delegation-modal.component';
import { DelegationStatus } from '../enums/delegation-status';
import { DelegationsQueryParamKey } from '../enums/delegations-query-param-key';
import { DelegationsRoute } from '../enums/delegations-route';
import type {
  Delegation,
  SimplifiedDelegation,
} from '../interfaces/delegation';
import { DelegationsService } from '../services/delegations.service';

export const DELEGATION_HEADER_ACTIONS = [
  'share',
  'showOnGantt',
  'showOnMap',
] as const;

@Service()
export class DelegationHelper {
  private readonly router = inject(Router);
  private readonly authStore = inject(AuthStore);
  private readonly modalService = inject(OnyxModalService);
  private readonly delegationService = inject(DelegationsService);
  private readonly toastService = inject(OnyxToastService);
  private readonly translateService = inject(TranslateService);
  private readonly injector = inject(Injector);

  private readonly I18N = 'delegations';

  public hasModule = computed(() => this.authStore.hasModule('delegations'));

  public getOptions(
    delegation: Delegation,
    options: HelperOptions,
    exclude: readonly (typeof DELEGATION_HEADER_ACTIONS)[number][] = [],
  ): OnyxOptionsGroup<() => void>[] {
    const isSettled = DelegationHelper.isSettled(delegation);
    const isArchived = DelegationHelper.isArchived(delegation);

    return [
      {
        id: '0.default',
        options: [
          ...([DelegationStatus.ACTIVE, DelegationStatus.PLANNED].includes(
            delegation.status.value,
          )
            ? [
                {
                  name: `${this.translateService.instant('labels.edit')}...`,
                  value: () => this.edit(delegation.uuid, options),
                  leftIcon: { name: 'edit-outlined', size: 20 },
                } as const,
              ]
            : []),
        ],
      },
      {
        id: '1.actions',
        options: [
          ...(!exclude.includes('share')
            ? [
                {
                  name: 'labels.share',
                  value: () => this.share(delegation),
                  leftIcon: { name: 'share-outlined', size: 20 },
                } as const,
              ]
            : []),
          ...(!exclude.includes('showOnGantt')
            ? [
                {
                  name: 'labels.showOnGantt',
                  value: () => this.showOnGantt(delegation, options),
                  leftIcon: { name: 'gantt-outlined', size: 20 },
                } as const,
              ]
            : []),
          ...(!exclude.includes('showOnMap')
            ? [
                {
                  name: 'labels.showOnMap',
                  value: () => this.showOnMap(delegation, options),
                  leftIcon: { name: 'map-view-outlined', size: 20 },
                } as const,
              ]
            : []),
          ...(delegation.status.value === DelegationStatus.PLANNED
            ? [
                {
                  name: `${this.translateService.instant('labels.activate')}...`,
                  value: () =>
                    this.openActionModal(
                      DelegationActionModalMode.ACTIVATE,
                      delegation,
                    ),
                  leftIcon: { name: 'check-outlined', size: 20 },
                } as const,
              ]
            : []),
          ...(delegation.status.value === DelegationStatus.ACTIVE
            ? [
                {
                  name: `${this.translateService.instant('labels.finish')}...`,
                  value: () =>
                    this.openActionModal(
                      DelegationActionModalMode.FINISH,
                      delegation,
                    ),
                  leftIcon: { name: 'check-outlined', size: 20 },
                  ...(!!delegation.relatedFleetSwap &&
                    DropdownHelper.getErrorFields(
                      'labels.disabledUntilSwapCompleted',
                    )),
                } as const,
              ]
            : []),
          ...(delegation.status.value === DelegationStatus.UNSETTLED
            ? [
                {
                  name: `${this.translateService.instant('labels.settle')}...`,
                  value: () =>
                    this.openActionModal(
                      DelegationActionModalMode.SETTLE,
                      delegation,
                    ),
                  leftIcon: { name: 'finance-outlined', size: 20 },
                } as const,
              ]
            : []),
        ],
      },
      {
        id: '2.cancel',
        options: [
          ...([DelegationStatus.PLANNED, DelegationStatus.ACTIVE].includes(
            delegation.status.value,
          )
            ? [
                {
                  name: 'labels.revoke',
                  value: () => this.cancelDelegation(delegation),
                  leftIcon: { name: 'clear-outlined', size: 20 },
                  leftIconColor: 'red',
                } as const,
              ]
            : []),
          ...(isSettled || isArchived
            ? [
                {
                  name: isArchived ? 'labels.unarchive' : 'labels.archive',
                  value: () => this.toggleArchived(delegation),
                  leftIcon: {
                    name: isArchived ? 'undo-outlined' : 'archives-outlined',
                    size: 20,
                  },
                  leftIconColor: isArchived ? 'black' : 'red',
                } as const,
              ]
            : []),
        ],
      },
    ].filter((group) => group.options.length);
  }

  public openModal(delegation: DelegationModalData): void {
    if (!this.hasModule()) return;

    this.modalService.open<DelegationModalData>(
      DelegationModalComponent,
      delegation,
    );
  }

  public edit(uuid: string, options: HelperOptions): void {
    this.router.navigateByUrl(
      DelegationsRoute.EDIT_DELEGATION.replace(':uuid', uuid),
      NavigationHelper.withOrigin(this.router),
    );
    CommonHelper.handleOptions(uuid, options);
  }

  public share(delegation: SimplifiedDelegation): void {
    ActionHelper.share(
      DelegationsRoute.DELEGATION_CARD.replace(':uuid', delegation.uuid),
      this.toastService,
    );
  }

  public showOnGantt(delegation: Delegation, options: HelperOptions): void {
    const today = DateTime.now().toISODate();

    const { start, end } = delegation;
    const date =
      start.date <= today && (!end.date || today <= end.date)
        ? today
        : start.date;

    CommonHelper.showOnGantt(
      this.router,
      'vehicle',
      delegation.vehicle.uuid,
      date,
    );
    CommonHelper.handleOptions(delegation, options);
  }

  public showOnMap(delegation: Delegation, options: HelperOptions): void {
    CommonHelper.showOnMap(this.router, delegation.vehicle.uuid, this.injector);
    CommonHelper.handleOptions(delegation, options);
  }

  public openActionModal(
    mode: DelegationActionModalMode,
    delegation: OnyxMaybeArray<Delegation | SimplifiedDelegation>,
    telematicsItem?: TelematicsItem,
  ): void {
    if (!this.hasModule()) return;

    this.modalService.open<DelegationActionModalData>(
      DelegationActionModalComponent,
      {
        mode,
        delegations: castArray(delegation),
        ...(telematicsItem && { telematicsItem }),
      },
    );
  }

  public toggleArchived(delegation: Delegation): void {
    const isArchived = DelegationHelper.isArchived(delegation);
    const action$ = isArchived
      ? this.delegationService.unarchiveDelegation(delegation.uuid)
      : this.delegationService.archiveDelegation(delegation.uuid);

    action$.subscribe(() =>
      this.toastService.showSuccess({
        message: this.translateService.instant(
          `${this.I18N}.delegation${isArchived ? 'Unarchived' : 'Archived'}`,
        ),
      }),
    );
  }

  private cancelDelegation(delegation: Delegation): void {
    this.delegationService.cancelDelegation(delegation.uuid).subscribe(() =>
      this.toastService.showSuccess({
        message: `${this.I18N}.delegationCanceled`,
      }),
    );
  }

  public static add(
    data: {
      telematicsItem?: TelematicsItem;
      telematicsDriver?: TelematicsDriver;
      vehicleUuid?: string;
      trailerUuid?: string;
      primaryDriverUuid?: string;
      secondaryDriverUuid?: string;
    },
    router: Router,
  ): void {
    const {
      telematicsItem,
      telematicsDriver,
      vehicleUuid,
      trailerUuid,
      primaryDriverUuid,
      secondaryDriverUuid,
    } = data ?? {};

    const isVehicle =
      telematicsItem && FleetTypeHelper.isVehicle(telematicsItem);
    const isTrailer =
      telematicsItem && FleetTypeHelper.isTrailer(telematicsItem);

    const telematicsVehicleUuid = isVehicle
      ? telematicsItem.uuid
      : telematicsItem?.assignedVehicle?.uuid;

    const telematicsTrailerUuid = isTrailer
      ? telematicsItem.uuid
      : telematicsItem?.assignedTrailer?.uuid;

    const [primaryDriver, secondaryDriver] = telematicsDriver
      ? (telematicsItem?.drivers.filter(
          ({ uuid }) => !telematicsDriver || uuid === telematicsDriver.uuid,
        ) ?? [])
      : (telematicsItem?.drivers ?? []);
    const telematicsPrimaryDriverUuid = primaryDriver?.uuid;
    const telematicsSecondaryDriverUuid = secondaryDriver?.uuid;

    const queryParams = chain({
      [DelegationsQueryParamKey.VEHICLE_UUID]:
        vehicleUuid ?? telematicsVehicleUuid,
      [DelegationsQueryParamKey.TRAILER_UUID]:
        trailerUuid ?? telematicsTrailerUuid,
      [DelegationsQueryParamKey.PRIMARY_DRIVER_UUID]:
        primaryDriverUuid ?? telematicsPrimaryDriverUuid,
      [DelegationsQueryParamKey.SECONDARY_DRIVER_UUID]:
        secondaryDriverUuid ?? telematicsSecondaryDriverUuid,
    })
      .omitBy((value) => !value)
      .value();

    const url = router
      .createUrlTree([DelegationsRoute.ADD_DELEGATION], { queryParams })
      .toString();

    ActionHelper.openLink(url);
  }

  public static hasEdit(
    delegation: SimplifiedDelegation | DelegationStatus,
  ): boolean {
    const status = isString(delegation) ? delegation : delegation.status.value;
    return [DelegationStatus.ACTIVE, DelegationStatus.PLANNED].includes(status);
  }

  public static isOutdated(
    status: DelegationStatus,
    type: 'start' | 'end',
    start?: Delegation['start'],
    end?: Delegation['end'],
  ): boolean {
    if (!DelegationHelper.hasEdit(status)) return false;

    const isStart = type === 'start';
    const delegationPeriod = isStart ? start : end;
    const isValid =
      (isStart && status === DelegationStatus.PLANNED) ||
      (!isStart && status === DelegationStatus.ACTIVE);
    if (!isValid || !delegationPeriod?.date) return false;

    const dateTime = DateTime.fromISO(
      `${delegationPeriod.date}T${delegationPeriod.time ?? DAY_TIME_RANGE.to}`,
    );

    return DateTime.now() > dateTime;
  }

  public static isPlanned(delegation: Delegation | DelegationStatus): boolean {
    const status = isString(delegation) ? delegation : delegation.status.value;
    return status === DelegationStatus.PLANNED;
  }

  public static isActive(
    delegation: OnyxOptional<SimplifiedDelegation | DelegationStatus>,
  ): boolean {
    if (!delegation) return false;

    const status = isString(delegation) ? delegation : delegation.status.value;
    return status === DelegationStatus.ACTIVE;
  }

  public static isFinished(delegation: Delegation | DelegationStatus): boolean {
    const status = isString(delegation) ? delegation : delegation.status.value;
    return [
      DelegationStatus.UNSETTLED,
      DelegationStatus.SETTLED,
      DelegationStatus.ARCHIVED,
    ].includes(status);
  }

  public static isSettled(delegation: Delegation | DelegationStatus): boolean {
    const status = isString(delegation) ? delegation : delegation.status.value;
    return status === DelegationStatus.SETTLED;
  }

  public static isArchived(delegation: Delegation | DelegationStatus): boolean {
    const status = isString(delegation) ? delegation : delegation.status.value;
    return status === DelegationStatus.ARCHIVED;
  }

  public static isCanceled(delegation: Delegation | DelegationStatus): boolean {
    const status = isString(delegation) ? delegation : delegation.status.value;
    return status === DelegationStatus.CANCELED;
  }

  public static isReturning(
    item: TelematicsDelegation | Delegation | SimplifiedDelegation,
  ): boolean {
    const delegation = 'delegation' in item ? item.delegation : item;
    return delegation.isReturning;
  }

  public static isDelegationType(
    delegation: SimplifiedDelegation | Delegation,
  ): delegation is Delegation {
    return 'vehicle' in delegation;
  }
}
